import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  clipVideoTimelineDuration,
  clipVideoTimelineLaneDuration,
  clipVideoPlaybackSpeed,
  effectiveClipAudioPlaybackSpeed,
  clipStoryBackgroundMusicPlaybackDuration,
  isStoryBackgroundMusicClip,
  storyBackgroundMusicSourceTime,
  effectiveNarrationAudioLaneVolume,
  isSceneBoundNarrationAudioClip,
  MIN_CLIP_PLAYBACK_SEC,
} from '@/lib/videoEditorPlayback';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';

function sameAudioSrc(a, b) {
  if (!a || !b) return a === b;
  try {
    const base = typeof window !== 'undefined' ? window.location.href : undefined;
    return new URL(a, base).href === new URL(b, base).href;
  } catch {
    return String(a) === String(b);
  }
}

function clipTimelineSceneId(clip) {
  const raw = String(clip?.sceneId || clip?.id || '');
  return raw.replace(/^audio-/, '').replace(/^seg-audio-/, '').split('-seg-')[0];
}

/** Min interval between React `currentTime` publishes during playback (avoids page re-renders). */
const UI_TIME_PUBLISH_INTERVAL_SEC = 0.25;

/*
 * Drift correction for media that plays on its own clock. Seeking a buffering element restarts
 * its fetch, so it keeps falling behind the transport; re-seeking it every frame leaves it silent.
 * Correct only elements that can play, with a cooldown, and resync a stalled one rarely.
 */
const PLAYING_DRIFT_SEC = 0.2;
const PAUSED_DRIFT_SEC = 0.2;
const SEEK_COOLDOWN_MS = 1000;
const STALLED_DRIFT_SEC = 1.5;
const STALLED_SEEK_COOLDOWN_MS = 2500;
/** Upcoming clips are positioned at their first sample this early so they start without a fetch gap. */
const PREROLL_SEC = 2;
const MEDIA_END_EPSILON_SEC = 0.05;
const HAVE_FUTURE_DATA = 3;

const lastSeekAt = new WeakMap();

function mediaCanPlay(el) {
  return el.readyState >= HAVE_FUTURE_DATA && !el.seeking;
}

function seekMedia(el, time, now) {
  try {
    el.currentTime = time;
  } catch {
    return;
  }
  lastSeekAt.set(el, now);
}

/** Whether a playing element that is `drift` seconds off the transport should be re-seeked now. */
function shouldCorrectDrift(el, drift, now) {
  const since = now - (lastSeekAt.get(el) || 0);
  if (mediaCanPlay(el)) return drift > PLAYING_DRIFT_SEC && since > SEEK_COOLDOWN_MS;
  return !el.seeking && drift > STALLED_DRIFT_SEC && since > STALLED_SEEK_COOLDOWN_MS;
}

/** Stable key so timeline edits / tab refresh that remap clip ids keep the same Audio element. */

function clipAudioElementKey(clip) {
  if (!clip?.url) return '';
  const resolvedSrc = mediaUrlForPlayback(clip.url);
  if (!resolvedSrc) return '';
  if (isStoryBackgroundMusicClip(clip)) {
    return `music:${clip.id}:${resolvedSrc}`;
  }
  if (clip.multishotPart != null && clip.id) {
    return `multishot:${clip.id}:${resolvedSrc}`;
  }
  // One HTML Audio node per timeline clip. Pause-cut windows share a scene + URL;
  // a shared element is paused by the inactive sibling and goes silent.
  const id = String(clip.id || '').trim();
  if (id) return `clip:${id}:${resolvedSrc}`;
  const scene = clipTimelineSceneId(clip);
  return scene ? `scene:${scene}:${resolvedSrc}` : `url:${resolvedSrc}`;
}

export function useSequentialPlayback({
  videoTracks,
  audioTracks,
  musicTracks = [[]],
  onTimeUpdate,
  onClipChange,
  onPlaybackEnd,
  /** (clip) => { muted, volume } for embedded video soundtrack in preview */
  getVideoPreviewAudioState,
  /** Multiplies all audio lane clip speeds in preview (0.25–4). */
  masterAudioPlaybackSpeed = 1,
  /** Master preview gain (0–1) for narration + music lanes. */
  masterPreviewVolume = 1,
  /** When true, mutes all timeline audio preview (not embedded video track). */
  masterPreviewMuted = false,
  /** When false, embedded video lane audio is forced silent regardless of per-clip state. */
  videoEmbeddedAudioEnabled = true,
  /** Called every RAF frame while playing: ({ transportTime, clip, clipIndex }). */
  onFrameTick,
}) {
  const mergedAudioTracks = useMemo(
    () => [...(audioTracks || []), ...(musicTracks || [])],
    [audioTracks, musicTracks],
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentClipIndex, setCurrentClipIndex] = useState(0);
  /** Decoded media length per clip; timeline metadata can underestimate real audio. */
  const [audioFileDurations, setAudioFileDurations] = useState({});

  const videoRef = useRef(null);
  const audioRefs = useRef(new Map());
  const animationFrameRef = useRef(null);
  const playbackStartTimeRef = useRef(0);
  const pausedAtRef = useRef(0);
  const isPlayingRef = useRef(false);
  const lastClipIndexRef = useRef(0);
  const currentTimeRef = useRef(0);
  const lastPublishedTimeRef = useRef(0);

  const mergedAudioTracksRef = useRef(mergedAudioTracks);
  mergedAudioTracksRef.current = mergedAudioTracks;
  const audioFileDurationsRef = useRef(audioFileDurations);
  audioFileDurationsRef.current = audioFileDurations;
  const masterAudioPlaybackSpeedRef = useRef(masterAudioPlaybackSpeed);
  masterAudioPlaybackSpeedRef.current = masterAudioPlaybackSpeed;
  const masterPreviewMutedRef = useRef(masterPreviewMuted);
  masterPreviewMutedRef.current = masterPreviewMuted;
  const masterPreviewVolumeRef = useRef(masterPreviewVolume);
  masterPreviewVolumeRef.current = masterPreviewVolume;
  const onTimeUpdateRef = useRef(onTimeUpdate);
  onTimeUpdateRef.current = onTimeUpdate;
  const onClipChangeRef = useRef(onClipChange);
  onClipChangeRef.current = onClipChange;
  const onPlaybackEndRef = useRef(onPlaybackEnd);
  onPlaybackEndRef.current = onPlaybackEnd;
  const onFrameTickRef = useRef(onFrameTick);
  onFrameTickRef.current = onFrameTick;
  const getVideoPreviewAudioStateRef = useRef(getVideoPreviewAudioState);
  getVideoPreviewAudioStateRef.current = getVideoPreviewAudioState;
  const videoEmbeddedAudioEnabledRef = useRef(videoEmbeddedAudioEnabled);
  videoEmbeddedAudioEnabledRef.current = videoEmbeddedAudioEnabled;

  const allClips = useMemo(
    () => (videoTracks?.[0] || []).slice().sort((a, b) => a.startTime - b.startTime),
    [videoTracks],
  );
  const allClipsRef = useRef(allClips);
  allClipsRef.current = allClips;

  const totalDuration = useMemo(
    () => {
      const videoEnd = allClips.reduce((max, clip) => {
        const clipEnd = clip.startTime + clipVideoTimelineLaneDuration(clip);
        return Math.max(max, clipEnd);
      }, 0);
      const audioEnd = mergedAudioTracks.flat().reduce((max, clip) => {
        const fileDur = Number(audioFileDurations[clip.id]) || 0;
        const dur = clipStoryBackgroundMusicPlaybackDuration(clip, fileDur, masterAudioPlaybackSpeed);
        const clipEnd = (Number(clip.startTime) || 0) + dur;
        return Math.max(max, clipEnd);
      }, 0);
      return Math.max(videoEnd, audioEnd);
    },
    [allClips, mergedAudioTracks, audioFileDurations, masterAudioPlaybackSpeed],
  );
  const totalDurationRef = useRef(totalDuration);
  totalDurationRef.current = totalDuration;

  const getTransportTime = useCallback(() => {
    if (!isPlayingRef.current) return pausedAtRef.current;
    return pausedAtRef.current + (performance.now() - playbackStartTimeRef.current) / 1000;
  }, []);

  const applyVideoPreviewAudio = useCallback((clip) => {
    const el = videoRef.current;
    if (!el || !clip?.videoUrl) return;
    const laneEnabled = videoEmbeddedAudioEnabledRef.current;
    const state = getVideoPreviewAudioStateRef.current?.(clip) ?? { muted: false, volume: 1 };
    const forceMuted = !laneEnabled || Boolean(state.muted);
    el.muted = forceMuted;
    const vol = Number(state.volume);
    el.volume = forceMuted ? 0 : (Number.isFinite(vol) ? Math.min(1, Math.max(0, vol)) : 1);
  }, []);

  useEffect(() => {
    const clip = allClips[currentClipIndex];
    if (clip?.videoUrl) applyVideoPreviewAudio(clip);
  }, [allClips, currentClipIndex, applyVideoPreviewAudio, getVideoPreviewAudioState, videoEmbeddedAudioEnabled]);

  const getClipAtTime = useCallback((time, clips = allClipsRef.current) => {
    let match = null;
    for (let i = 0; i < clips.length; i++) {
      const clip = clips[i];
      const clipEnd = clip.startTime + clipVideoTimelineLaneDuration(clip);
      if (time >= clip.startTime && time < clipEnd) {
        match = { clip, index: i };
      }
    }
    return match;
  }, []);

  /** Map timeline time → source file time (seconds), clamped to trim window. */
  const timelineToMediaTime = useCallback((timelineTime, clip) => {
    if (!clip?.videoUrl) return 0;
    const spd = clipVideoPlaybackSpeed(clip);
    const ts = clip.trimStart ?? 0;
    const dur = clip.duration ?? clip.trimEnd ?? 0;
    const te = clip.trimEnd != null ? clip.trimEnd : dur;
    const local = timelineTime - clip.startTime;
    const media = ts + local * spd;
    const hi = Math.max(ts + 0.001, te);
    return Math.min(hi, Math.max(ts, media));
  }, []);

  const previewLaneVolume = useCallback((clip) => {
    if (masterPreviewMutedRef.current || clip?.muted) return 0;
    let clipVol = effectiveNarrationAudioLaneVolume(clip);
    if (isStoryBackgroundMusicClip(clip)) {
      clipVol = Math.min(1, clipVol * 2.5);
    }
    const master = Number(masterPreviewVolumeRef.current);
    const m = Number.isFinite(master) ? Math.min(1, Math.max(0, master)) : 1;
    return Math.min(1, clipVol * m);
  }, []);

  const syncAudio = useCallback((time, playing) => {
    const tracks = mergedAudioTracksRef.current;
    const durations = audioFileDurationsRef.current;
    const masterSpeed = masterAudioPlaybackSpeedRef.current;
    const now = performance.now();
    const entries = [];
    const activeKeys = new Set();
    tracks.flat().forEach((clip) => {
      const key = clipAudioElementKey(clip);
      const audio = key ? audioRefs.current.get(key) : null;
      if (!audio) return;
      const fileDur = (Number.isFinite(audio.duration) && audio.duration > 0)
        ? audio.duration
        : (Number(durations[clip.id]) || 0);
      const spd = effectiveClipAudioPlaybackSpeed(clip, masterSpeed);
      let timelineW = clipStoryBackgroundMusicPlaybackDuration(clip, fileDur, masterSpeed);
      if (isSceneBoundNarrationAudioClip(clip)) {
        const metaCap = Number(clip.duration) || 0;
        if (metaCap > 0) timelineW = Math.max(MIN_CLIP_PLAYBACK_SEC / spd, metaCap / spd);
      }
      const start = Number(clip.startTime) || 0;
      const active = time >= start && time < start + timelineW + 0.03;
      if (active) activeKeys.add(key);
      entries.push({ clip, key, audio, fileDur, spd, start, active });
    });

    entries.forEach(({ clip, key, audio, fileDur, spd, start, active }) => {
      const isMusic = isStoryBackgroundMusicClip(clip);
      const srcOffset = isMusic ? 0 : Math.max(0, Number(clip.audioSourceTrimStart) || 0);
      if (!active) {
        if (activeKeys.has(key)) return;
        if (!audio.paused) audio.pause();
        const lead = start - time;
        if (playing && lead > 0 && lead <= PREROLL_SEC && !audio.seeking) {
          if (audio.preload !== 'auto') audio.preload = 'auto';
          if (Math.abs(audio.currentTime - srcOffset) > MEDIA_END_EPSILON_SEC) seekMedia(audio, srcOffset, now);
        }
        return;
      }

      const local = time - start;
      let clipTime;
      if (isMusic && fileDur > 0.05) {
        clipTime = storyBackgroundMusicSourceTime(local, clip, fileDur, masterSpeed);
      } else {
        const metaDur = Number(clip.duration) || 0;
        const sd = Number(clip.sourceDuration) || metaDur;
        const maxSrc = Math.max(fileDur, sd, srcOffset + metaDur);
        const cap = Math.max(srcOffset, maxSrc - 0.04);
        clipTime = Math.min(Math.max(srcOffset, srcOffset + local * spd), cap);
      }
      if (Math.abs((audio.playbackRate || 1) - spd) > 0.02) audio.playbackRate = spd;

      // The slot can outlast the file; play() on an ended element would restart it from 0.
      const pastEnd = !audio.loop && fileDur > 0 && clipTime >= fileDur - MEDIA_END_EPSILON_SEC;
      if (pastEnd) {
        if (!audio.paused) audio.pause();
        return;
      }

      const drift = Math.abs(audio.currentTime - clipTime);
      if (!playing) {
        if (drift > PAUSED_DRIFT_SEC) seekMedia(audio, clipTime, now);
        if (!audio.paused) audio.pause();
        return;
      }
      if (audio.paused) {
        if (drift > MEDIA_END_EPSILON_SEC && !audio.seeking) seekMedia(audio, clipTime, now);
        audio.play().catch(() => {});
      } else if (shouldCorrectDrift(audio, drift, now)) {
        seekMedia(audio, clipTime, now);
      }
    });
  }, []);

  const syncAudioRef = useRef(syncAudio);
  syncAudioRef.current = syncAudio;

  // Manage audio elements + capture decoded duration (can exceed clip.duration from API).
  useEffect(() => {
    const currentAudioRefs = audioRefs.current;
    const allAudioClips = mergedAudioTracks.flat();
    const activeKeys = new Set();
    const metaListeners = [];

    allAudioClips.forEach((clip) => {
      if (!clip?.url) return;
      const resolvedSrc = mediaUrlForPlayback(clip.url);
      if (!resolvedSrc) return;
      if (
        resolvedSrc.startsWith('/')
        && !resolvedSrc.startsWith('//')
        && typeof window !== 'undefined'
        && window.location?.protocol === 'file:'
      ) {
        return;
      }
      const key = clipAudioElementKey(clip);
      if (!key) return;
      activeKeys.add(key);

      let audio = currentAudioRefs.get(key);
      if (!audio) {
        audio = new Audio(resolvedSrc);
        audio.loop = false;
        currentAudioRefs.set(key, audio);
      } else if (!sameAudioSrc(audio.src, resolvedSrc)) {
        const resumeTime = audio.currentTime;
        const shouldResume = isPlayingRef.current && !audio.paused;
        audio.pause();
        audio.src = resolvedSrc;
        audio.load();
        const resume = () => {
          if (Number.isFinite(resumeTime) && resumeTime > 0) {
            try { audio.currentTime = resumeTime; } catch { /* ignore */ }
          }
          if (shouldResume || isPlayingRef.current) {
            syncAudioRef.current(getTransportTime(), isPlayingRef.current);
          }
        };
        if (audio.readyState >= 2) resume();
        else audio.addEventListener('loadeddata', resume, { once: true });
      }
      audio.volume = previewLaneVolume(clip);
      audio.muted = Boolean(clip.muted) || masterPreviewMutedRef.current;

      const publish = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setAudioFileDurations((prev) => (
            prev[clip.id] === audio.duration ? prev : { ...prev, [clip.id]: audio.duration }
          ));
        }
      };
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        publish();
      } else {
        const onMeta = () => publish();
        audio.addEventListener('loadedmetadata', onMeta, { once: true });
        metaListeners.push(() => audio.removeEventListener('loadedmetadata', onMeta));
      }
    });

    const orphanKeys = [];
    currentAudioRefs.forEach((_, key) => {
      if (!activeKeys.has(key)) orphanKeys.push(key);
    });
    orphanKeys.forEach((key) => {
      currentAudioRefs.get(key)?.pause();
      currentAudioRefs.delete(key);
    });

    if (isPlayingRef.current) {
      const time = getTransportTime();
      pausedAtRef.current = time;
      playbackStartTimeRef.current = performance.now();
      syncAudioRef.current(time, true);
    } else {
      syncAudioRef.current(pausedAtRef.current, false);
    }

    return () => {
      metaListeners.forEach((fn) => fn());
    };
  }, [mergedAudioTracks, previewLaneVolume, masterPreviewMuted, getTransportTime]);

  /** Loop story bed when the MP3 is shorter than the video slot. */
  useEffect(() => {
    mergedAudioTracks.flat().forEach((clip) => {
      const key = clipAudioElementKey(clip);
      const audio = key ? audioRefs.current.get(key) : null;
      if (!audio) return;
      if (!isStoryBackgroundMusicClip(clip)) {
        audio.loop = false;
        return;
      }
      const playSpan = Number(clip.duration) || 0;
      const fd = (Number.isFinite(audio.duration) && audio.duration > 0)
        ? audio.duration
        : (Number(audioFileDurations[clip.id]) || 0);
      audio.loop = playSpan > fd + 0.05;
    });
  }, [mergedAudioTracks, audioFileDurations]);

  const tick = useCallback(() => {
    if (!isPlayingRef.current) return;
    const elapsed = (performance.now() - playbackStartTimeRef.current) / 1000;
    const time = pausedAtRef.current + elapsed;
    const duration = totalDurationRef.current;

    if (time >= duration) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      currentTimeRef.current = duration;
      lastPublishedTimeRef.current = duration;
      setCurrentTime(duration);
      pausedAtRef.current = 0;
      onTimeUpdateRef.current?.(duration);
      onPlaybackEndRef.current?.();
      syncAudioRef.current(duration, false);
      return;
    }

    currentTimeRef.current = time;
    const result = getClipAtTime(time);
    const clipChanged = result ? lastClipIndexRef.current !== result.index : false;
    if (
      clipChanged
      || Math.abs(time - lastPublishedTimeRef.current) >= UI_TIME_PUBLISH_INTERVAL_SEC
    ) {
      lastPublishedTimeRef.current = time;
      setCurrentTime(time);
      onTimeUpdateRef.current?.(time);
    }
    syncAudioRef.current(time, true);

    if (result) {
      if (clipChanged) {
        lastClipIndexRef.current = result.index;
        setCurrentClipIndex(result.index);
        onClipChangeRef.current?.(result.index, 0);
      }
      const videoEl = videoRef.current;
      if (videoEl) {
        const clipLocalTime = timelineToMediaTime(time, result.clip);
        applyVideoPreviewAudio(result.clip);
        if (result.clip.videoUrl) {
          const rate = clipVideoPlaybackSpeed(result.clip);
          if (Math.abs((videoEl.playbackRate || 1) - rate) > 0.02) videoEl.playbackRate = rate;
        }
        const now = performance.now();
        const drift = Math.abs(videoEl.currentTime - clipLocalTime);
        if (clipChanged || shouldCorrectDrift(videoEl, drift, now)) {
          seekMedia(videoEl, clipLocalTime, now);
        }
        if (result.clip.videoUrl && videoEl.paused) {
          videoEl.play().catch(() => {});
        } else if (!result.clip.videoUrl && !videoEl.paused) {
          videoEl.pause();
        }
      }
    } else if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }

    onFrameTickRef.current?.({
      transportTime: time,
      clip: result?.clip ?? null,
      clipIndex: result?.index ?? -1,
    });

    animationFrameRef.current = requestAnimationFrame(tick);
  }, [getClipAtTime, timelineToMediaTime, applyVideoPreviewAudio]);

  useEffect(() => {
    if (isPlaying) {
      isPlayingRef.current = true;
      playbackStartTimeRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(tick);
    } else {
      isPlayingRef.current = false;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    }
    return () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); };
  }, [isPlaying, tick]);

  /** Stop all preview media when leaving the editor (route change unmounts the hook). */
  useEffect(() => {
    const audioMap = audioRefs.current;
    const videoEl = videoRef;
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      isPlayingRef.current = false;
      audioMap.forEach((audio) => {
        try {
          audio.pause();
          audio.loop = false;
          audio.src = '';
          audio.load();
        } catch {
          /* ignore */
        }
      });
      audioMap.clear();
      if (videoEl.current) {
        try {
          videoEl.current.pause();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  const play = useCallback(() => {
    const duration = totalDurationRef.current;
    const transport = getTransportTime();
    if (transport >= duration) {
      pausedAtRef.current = 0;
      currentTimeRef.current = 0;
      lastPublishedTimeRef.current = 0;
      setCurrentTime(0);
      setCurrentClipIndex(0);
    } else {
      pausedAtRef.current = transport;
      currentTimeRef.current = transport;
      lastPublishedTimeRef.current = transport;
    }
    const result = getClipAtTime(pausedAtRef.current);
    if (videoRef.current && result?.clip?.videoUrl) {
      applyVideoPreviewAudio(result.clip);
      videoRef.current.currentTime = timelineToMediaTime(pausedAtRef.current, result.clip);
      videoRef.current.play().catch(() => {});
    }
    syncAudioRef.current(pausedAtRef.current, true);
    playbackStartTimeRef.current = performance.now();
    setIsPlaying(true);
  }, [getClipAtTime, getTransportTime, timelineToMediaTime, applyVideoPreviewAudio]);

  const pause = useCallback(() => {
    const t = getTransportTime();
    pausedAtRef.current = t;
    currentTimeRef.current = t;
    lastPublishedTimeRef.current = t;
    setCurrentTime(t);
    setIsPlaying(false);
    syncAudioRef.current(t, false);
    if (videoRef.current) videoRef.current.pause();
  }, [getTransportTime]);

  const togglePlay = useCallback(() => {
    if (isPlayingRef.current) pause();
    else play();
  }, [play, pause]);

  const seek = useCallback((time) => {
    const duration = totalDurationRef.current;
    const t = Math.max(0, Math.min(time, duration));
    currentTimeRef.current = t;
    lastPublishedTimeRef.current = t;
    setCurrentTime(t);
    pausedAtRef.current = t;
    if (isPlayingRef.current) {
      playbackStartTimeRef.current = performance.now();
    }
    const result = getClipAtTime(t);
    if (result) {
      lastClipIndexRef.current = result.index;
      setCurrentClipIndex(result.index);
      onClipChangeRef.current?.(result.index, 0);
    }
    if (videoRef.current && result?.clip?.videoUrl) {
      applyVideoPreviewAudio(result.clip);
      videoRef.current.currentTime = timelineToMediaTime(t, result.clip);
    } else if (videoRef.current) {
      videoRef.current.pause();
    }
    syncAudioRef.current(t, isPlayingRef.current);
    onTimeUpdateRef.current?.(t);
  }, [getClipAtTime, timelineToMediaTime, applyVideoPreviewAudio]);

  const stop = useCallback(() => {
    setIsPlaying(false);
    pausedAtRef.current = 0;
    currentTimeRef.current = 0;
    lastPublishedTimeRef.current = 0;
    setCurrentTime(0);
    setCurrentClipIndex(0);
    lastClipIndexRef.current = 0;
    syncAudioRef.current(0, false);
    if (videoRef.current) videoRef.current.pause();
  }, []);

  const currentClip = allClips[currentClipIndex] || null;

  return {
    isPlaying, currentTime, totalDuration, currentClipIndex,
    currentClip, allClips, play, pause, togglePlay, seek, stop, videoRef,
    audioFileDurations, getTransportTime, currentTimeRef,
  };
}
